import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { QuotationSubject } from '@euler/model/task-detail';
import { memo, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { ReadonlyDiscountButton } from './DiscountButton';
import { PriceInfoView } from './PriceInfoView';

export const ReadonlyQuotationSubjectSubTotalInfo = memo(
  ({
    subject,
    style,
  }: {
    subject: QuotationSubject;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    const subTotalInfo = useMemo(() => {
      if (!subject.included) return null;

      let originalMaterialSubTotal = 0;
      let discountedMaterialSubTotal = 0;
      let originalManHourCostSubTotal = 0;
      let discountedManHourCostSubTotal = 0;
      let originalSubTotal = 0;
      let discountedSubTotal = 0;

      for (const item of subject.items) {
        if (!item.included) continue;
        if (item.price == null) return null;
        originalManHourCostSubTotal += item.price ?? 0;
        discountedManHourCostSubTotal += item.discountPrice ?? item.price ?? 0;
        for (const material of item.materials) {
          if (!material.included) continue;
          if (material.price == null) return null;
          originalMaterialSubTotal +=
            (material.price ?? 0) * (material.qty ?? 1);
          discountedMaterialSubTotal +=
            (material.discountPrice ?? material.price ?? 0) *
            (material.qty ?? 1);
        }
      }

      originalSubTotal = originalMaterialSubTotal + originalManHourCostSubTotal;
      discountedSubTotal =
        discountedMaterialSubTotal + discountedManHourCostSubTotal;

      if (subject.additionalManHourCostSubTotalDiscount != null) {
        discountedSubTotal -= subject.additionalManHourCostSubTotalDiscount;
      }
      if (subject.additionalMaterialSubTotalDiscount != null) {
        discountedSubTotal -= subject.additionalMaterialSubTotalDiscount;
      }

      return {
        originalManHourCostSubTotal,
        discountedManHourCostSubTotal,
        originalMaterialSubTotal,
        discountedMaterialSubTotal,
        originalSubTotal,
        discountedSubTotal,
      };
    }, [
      subject.additionalManHourCostSubTotalDiscount,
      subject.additionalMaterialSubTotalDiscount,
      subject.included,
      subject.items,
    ]);

    return (
      <StackLayout style={style} gap={rpx(5)}>
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <StackLayout direction="row" alignItems="center">
            <SubTotalLabel label="工时费" />
            <PriceInfoView
              price={subTotalInfo?.originalManHourCostSubTotal}
              discountPrice={subTotalInfo?.discountedManHourCostSubTotal}
              additionalDiscount={subject.additionalManHourCostSubTotalDiscount}
            />
            <ReadonlyDiscountButton
              price={subTotalInfo?.originalManHourCostSubTotal}
              discountPrice={subTotalInfo?.discountedManHourCostSubTotal}
              discount={subject.additionalManHourCostSubTotalDiscount}
              css={`
                margin-left: ${rpx(5)}px;
              `}
            />
          </StackLayout>
          <StackLayout direction="row" alignItems="center">
            <SubTotalLabel label="配件费" />
            <PriceInfoView
              price={subTotalInfo?.originalMaterialSubTotal}
              discountPrice={subTotalInfo?.discountedMaterialSubTotal}
              additionalDiscount={subject.additionalMaterialSubTotalDiscount}
            />
            <ReadonlyDiscountButton
              price={subTotalInfo?.originalMaterialSubTotal}
              discountPrice={subTotalInfo?.discountedMaterialSubTotal}
              discount={subject.additionalMaterialSubTotalDiscount}
              css={`
                margin-left: ${rpx(5)}px;
              `}
            />
          </StackLayout>
        </StackLayout>
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <View />
          <StackLayout direction="row" alignItems="center">
            <SubTotalLabel label="小计" />
            <PriceInfoView
              price={subTotalInfo?.originalSubTotal}
              discountPrice={subTotalInfo?.discountedSubTotal}
              additionalDiscount={subject.additionalSubTotalDiscount}
              priceStyle={styles.subTotalPrice}
              originalPriceStyle={styles.originalSubTotalPrice}
              style={{
                alignSelf: 'baseline',
              }}
            />
            <ReadonlyDiscountButton
              price={subTotalInfo?.originalSubTotal}
              discountPrice={subTotalInfo?.discountedSubTotal}
              discount={subject.additionalSubTotalDiscount}
              css={`
                margin-left: ${rpx(5)}px;
              `}
            />
          </StackLayout>
        </StackLayout>
      </StackLayout>
    );
  },
);

const SubTotalLabel = memo(
  ({ label, style }: { label: string; style?: StyleProp<TextStyle> }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Typography.Text
        style={style}
        color="#777"
        size={rpx(14)}
      >{`${label}: `}</Typography.Text>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        subTotalPrice: {
          fontSize: rpx(18),
          color: StatusColors.Danger,
        },
        originalSubTotalPrice: {
          color: '#ccc',
        },
      }),
    [rpx],
  );
};
